import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';


/**
 * A custom element that allows the user to clean a search input.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchPageInput - The search input element.
 * @extends {Component<Refs>}
 */
class SearchPageInputComponent extends Component {
  requiredRefs = ['searchPageInput'];

  connectedCallback() {
    super.connectedCallback();

    const initialTerm = new URL(window.location.href).searchParams.get('q')?.trim();

    if (initialTerm) {
      void this.#fetchSuggestions(initialTerm);
    }
  }

  /**
   * Fetches predictive search suggestions for the given term and renders them
   * immediately after this element in the DOM, replacing any existing suggestions.
   *
   * @param {string} searchTerm
   * @returns {Promise<void>}
   */
  async #fetchSuggestions(searchTerm) {
    if (!searchTerm) return;

    const sectionId = /** @type {HTMLElement} */ (this).dataset.sectionId;
    if (!sectionId) return;

    const suggestUrl = new URL(Theme.routes.predictive_search_url, location.origin);
    suggestUrl.searchParams.set('q', searchTerm);
    suggestUrl.searchParams.set('resources[type]', 'query');
    suggestUrl.searchParams.set('resources[limit]', '5');

    const html = await sectionRenderer.getSectionHTML(sectionId, false, suggestUrl);
    if (!html) return;

    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const suggestions = parsed.querySelector('.search-suggestions');

    const host = /** @type {HTMLElement} */ (this).parentElement?.parentElement;
    host?.querySelector('.search-suggestions')?.remove();

    if (suggestions) {
      /** @type {HTMLElement} */ (this).insertAdjacentElement('afterend', suggestions);
    }
  }

  /**
   * Handles the keydown event on the search input and resets the search when
   * empty and Escape is pressed.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown = debounce((event) => {
    const value = this.refs.searchPageInput.value.trim();

    if (event.key === 'Escape' && value === '') {
      this.#submitEmptySearch();
      return;
    }

    void this.#fetchSuggestions(value);

  }, 100);

  #submitEmptySearch() {
    const searchInput = this.refs.searchPageInput;

    searchInput.focus();
    searchInput.value = '';

    if (this.#isEmptyState()) return;

    searchInput.form?.submit();
  }

  #isEmptyState = () => {
    const url = new URL(window.location.href);
    const queryParam = url.searchParams.get('q') ?? '';

    return queryParam.trim() === '';
  };
}

if (!customElements.get('search-page-input-component')) {
  customElements.define('search-page-input-component', SearchPageInputComponent);
}
